import {
  button,
  div,
  fnstate,
  goTo,
  h1,
  input,
  label,
  pathParameters,
  span
} from '@srfnstack/fntags'
import { ImageViewer } from '../../components/ImageViewer.js'
import { lastJobUpdate } from '../../state.js'
import { CreditButton } from '../../components/CreditButton.js'
import { NumberSpinner } from '../../components/NumberSpinner.js'
import { AssetGrid } from './AssetGrid.js'
import { ModelSelector, ALL_IMAGE_PROVIDERS, MOCKUP_IMAGE_PROVIDERS } from '../../components/ModelSelector.js'
import { fetchDefaultImageModel } from '../../utils/settings.js'

export const ArtworkReviewDetail = () => {
  const promptId = pathParameters().promptId
  const selectedPrompt = fnstate(null)
  const assets = fnstate([], a => a.id)
  const loading = fnstate(true)
  const showRejected = fnstate(false)
  const selectedAsset = fnstate(null)
  const selectedAssets = fnstate([])
  const productTypes = fnstate([])
  const batchSize = fnstate(5)
  const genMoreMultipliers = fnstate({ batch_size: 5 })
  batchSize.subscribe(b => genMoreMultipliers({ batch_size: b }))
  const genMoreModel = fnstate('bfl_flux2pro')
  fetchDefaultImageModel().then(m => genMoreModel(m))

  const handleJobUpdate = (event) => {
    if (!event) return

    const isRelevant = event.payload?.concept_prompt_id?.toString() === promptId.toString()

    if (isRelevant) {
      fetchPromptAndAssets()
    }
  }

  // Store unsubscribe so stale instances self-clean when root element leaves DOM.
  // Without this, every page navigation stacks another subscriber on the global
  // lastJobUpdate state, causing cascading fetches on each job update.
  let rootEl = null
  const unsubRef = { fn: null }
  unsubRef.fn = lastJobUpdate.subscribe((event) => {
    if (rootEl && !rootEl.isConnected) {
      unsubRef.fn?.()
      unsubRef.fn = null
      return
    }
    handleJobUpdate(event)
  })

  const getFilteredAssets = () => {
    const all = assets()
    const show = showRejected()
    return all.filter(a => {
      const item = a
      if (item.type === 'PENDING_JOB' || item.status === 'PENDING') return true
      return show || !item.rejected
    })
  }

  const navigate = (direction) => {
    const filtered = getFilteredAssets()
    if (filtered.length === 0) return

    const current = selectedAsset()
    const currentIndex = filtered.findIndex(a => a.id === current?.id)

    let nextIndex = currentIndex + direction
    if (nextIndex >= filtered.length) nextIndex = 0
    if (nextIndex < 0) nextIndex = filtered.length - 1

    selectedAsset(filtered[nextIndex])
  }

  const toggleRejectFromViewer = async (asset) => {
    const action = asset.rejected ? 'unreject' : 'reject'
    const opts = { method: 'POST' }
    if (action === 'reject') {
      const note = await askRejectReason(1)
      opts.headers = { 'Content-Type': 'application/json' }
      opts.body = JSON.stringify({ note })
    }
    const res = await fetch(`/api/assets/${asset.id}/${action}`, opts)
    if (res.ok) {
      if (!asset.rejected) {
        navigate(1)
      }
      assets(
        assets().map(a => {
          if (a.id === asset.id) {
            a.rejected = !a.rejected
          }
          return a
        })
      )
    }
  }

  const fetchPromptAndAssets = async () => {
    if (assets().length === 0) loading(true)
    try {
      const pRes = await fetch(`/api/prompts/${promptId}?_=${Date.now()}`)
      if (pRes.ok) {
        selectedPrompt(await pRes.json())
      }

      const aRes = await fetch(`/api/assets?prompt_id=${promptId}&_=${Date.now()}`)
      if (aRes.ok) {
        assets(await aRes.json())
      }

      const typesRes = await fetch('/api/system/product-types')
      if (typesRes.ok) {
        productTypes(await typesRes.json())
      }
    } catch (err) {
      console.error('Failed to fetch:', err)
    } finally {
      loading(false)
    }
  }

  const handleGenerateMore = async () => {
    const res = await fetch(`/api/prompts/${promptId}/generate-more`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ batch_size: batchSize(), image_model: genMoreModel() })
    })

    if (res.ok) {
      fetchPromptAndAssets()
    } else {
      await window.alert('Failed to enqueue generation job.')
    }
  }

  const handleEnhance = async (asset) => {
    const isMasking = window.__isMasking()
    const correction = await window.prompt(isMasking ? 'Any specific corrections for the masked area? (optional):' : 'Any specific enhancements for the whole image? (optional):')
    if (correction === null) return

    const formData = new FormData()
    if (isMasking) {
      const maskBlob = await window.__getMaskBlob()
      formData.append('mask', maskBlob, 'mask.png')
    }
    formData.append('user_correction', correction)

    const res = await fetch(`/api/assets/${asset.id}/enhance`, {
      method: 'POST',
      body: formData
    })

    if (res.ok) {
      if (isMasking) window.__toggleMasking()
      selectedAsset(null)
      fetchPromptAndAssets()
    } else {
      await window.alert('Failed to trigger enhancement.')
    }
  }

  const handlePromote = async (assetId, productType) => {
    const opts = await window.promptMockupOptions(genMoreModel(), MOCKUP_IMAGE_PROVIDERS, `Promote to ${productType}`)
    if (opts === null) return

    const res = await fetch(`/api/assets/${assetId}/promote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ product_type: productType, mockup_model: opts.model })
    })

    if (res.ok) {
      const data = await res.json()
      selectedAsset(null)
      const isInitRequired = productType?.toLowerCase().includes('shirt')
      goTo(isInitRequired ? `/product-init/${data.product_id}` : `/product-review/${data.product_id}`)
    } else {
      const result = await res.json()
      await window.alert('Failed to promote to product: ' + (result.error || 'Unknown error'))
    }
  }

  const handleBulkPromote = async () => {
    const sel = selectedAssets()
    if (sel.length === 0) return

    const types = productTypes()
    if (types.length === 0) {
      await window.alert('Product types not yet loaded, please wait.')
      return
    }

    const options = types.map(t => t.keyword)
    const productType = await window.promptSelect(`Promote ${sel.length} items to:`, options, options[0], 'Bulk Promote')
    if (productType === null) return

    const opts = await window.promptMockupOptions(genMoreModel(), MOCKUP_IMAGE_PROVIDERS, `Bulk Promote — ${sel.length} items`)
    if (opts === null) return

    const res = await fetch('/api/assets/bulk-promote', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ asset_ids: sel, product_type: productType, mockup_model: opts.model })
    })

    if (res.ok) {
      const data = await res.json()
      selectedAssets([])
      if (data.skipped > 0) {
        await window.alert(`Promoted ${data.promoted} items. ${data.skipped} already promoted as this type were skipped.`)
      }
      fetchPromptAndAssets()
      goTo('/product-review')
    } else {
      const result = await res.json()
      await window.alert('Failed to perform bulk promotion: ' + (result.error || 'Unknown error'))
    }
  }

  const handleRejectArtwork = async () => {
    const prompt = selectedPrompt()
    const isRejected = prompt?.status === 'REJECTED'
    if (isRejected) {
      if (!await window.confirm('Restore this artwork set?')) return
      const res = await fetch(`/api/prompts/${promptId}/unreject`, { method: 'POST' })
      if (res.ok) {
        selectedPrompt({ ...prompt, status: 'COMPLETED' })
      } else {
        await window.alert('Failed to restore artwork.')
      }
    } else {
      if (!await window.confirm('Reject this entire artwork set? It will be hidden from the artwork manager.')) return
      const res = await fetch(`/api/prompts/${promptId}/reject`, { method: 'POST' })
      if (res.ok) {
        goTo('/artwork-review')
      } else {
        await window.alert('Failed to reject artwork.')
      }
    }
  }

  // Ask for the reason at the moment of the decision. 246 assets sit in
  // production rejected by a human with nothing recorded but a boolean — every
  // one of those was a free label thrown away, and the calibration set had to be
  // rebuilt from scratch in a separate sitting because none of them survived.
  //
  // A note, not a chip picker: of 94 labels gathered in a dedicated session, 88
  // carried a written note and only 6 used a defect chip. The operator describes
  // what is wrong in their own words, so that is what to ask for. It stays
  // optional — cancelling still rejects, because a reject must never be blocked
  // on filling in a form.
  const askRejectReason = async (count) => {
    const label = count > 1
      ? `Why are these ${count} being rejected? (optional — one line helps tune the gate)`
      : 'Why? (optional — one line helps tune the gate)'
    const note = await window.prompt(label, '')
    return typeof note === 'string' ? note.trim() : ''
  }

  const handleBulkReject = async () => {
    const sel = selectedAssets()
    if (sel.length === 0) return

    if (await window.confirm(`Are you sure you want to reject ${sel.length} items?`)) {
      const note = await askRejectReason(sel.length)
      for (const id of sel) {
        await fetch(`/api/assets/${id}/reject`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note })
        })
      }
      await window.alert(`Rejected ${sel.length} items.`)
      selectedAssets([])
      fetchPromptAndAssets()
    }
  }

  const toggleReject = async (assetState) => {
    if (!assetState) return
    const asset = assetState()

    const action = asset.rejected ? 'unreject' : 'reject'
    const res = await fetch(`/api/assets/${asset.id}/${action}`, { method: 'POST' })
    if (res.ok) {
      asset.rejected = !asset.rejected
      assetState(asset)
    }
  }

  fetchPromptAndAssets()

  rootEl = div({ class: 'p-4 md:p-6 flex flex-col min-h-[calc(100vh-64px)]' },
    ImageViewer(selectedAsset, () => navigate(1), () => navigate(-1), (asset) => {
      const toTitle = s => s.replace(/\b\w/g, c => c.toUpperCase())
      const types = productTypes()
      const isIsolated = asset.background_type === 'ISOLATED_BLACK' || asset.background_type === 'ISOLATED_WHITE'
      const compatible = isIsolated
        ? types
        : types.filter(t => (t.product_detail_config?.artwork_class || 'SURFACE_ART') !== 'ISOLATED_ART')
      const promotedMap = new Map((asset.product_concepts || []).map(pc => [pc.product_type.toLowerCase(), pc.id]))
      const toPromote = compatible.filter(t => !promotedMap.has(t.keyword.toLowerCase()))

      return [
        window.__isMasking.bindAs(m => m
          ? button({
            class: 'btn btn-error btn-sm',
            onclick: () => window.__toggleMasking()
          }, 'Cancel')
          : button({
            class: 'btn btn-ghost text-white btn-sm',
            onclick: () => window.__toggleMasking()
          }, 'Paint Mask')
        ),
        CreditButton({
          actionCode: 'IMAGE_ENHANCE',
          class: 'btn btn-primary btn-sm',
          onclick: async () => handleEnhance(asset),
          label: window.__isMasking.bindAs(m => m ? 'Apply Mask & Enhance' : 'Enhance')
        }),
        window.__isMasking.bindAs(m => m
          ? span()
          : div({ class: 'flex gap-2 flex-wrap' },
            ...toPromote.map(type => CreditButton({
              actionCode: 'IMAGE_UPSCALE',
              class: 'btn btn-primary btn-sm',
              onclick: async () => handlePromote(asset.id, type.keyword),
              label: `Promote: ${toTitle(type.keyword)}`
            })),
            ...[...promotedMap.entries()].map(([type, id]) => button({
              class: 'btn btn-success btn-sm',
              onclick: () => { selectedAsset(null); goTo(`/product-review/${id}`) }
            }, `View: ${toTitle(type)}`))
          )
        ),
        window.__isMasking.bindAs(m => m
          ? span()
          : button({
            class: `btn ${asset.rejected ? 'btn-success' : 'btn-error'} btn-sm`,
            onclick: () => toggleRejectFromViewer(asset)
          }, asset.rejected ? 'Unreject Image' : 'Reject Image')
        )
      ]
    }, () => div({ class: 'form-control bg-base-100/20 px-3 py-1 rounded-full' },
      label({ class: 'label cursor-pointer gap-2 py-0' },
        span({ class: 'label-text text-white text-xs' }, 'View Rejected'),
        input({
          type: 'checkbox',
          class: 'toggle toggle-primary toggle-xs',
          checked: showRejected(),
          onchange: (e) => {
            showRejected(e.target.checked)
            const current = selectedAsset()
            if (!e.target.checked && current?.().rejected) {
              const all = assets()
              const currentIndex = all.findIndex(a => a.id === current?.().id)
              for (let i = 1; i < all.length; i++) {
                let idx = currentIndex + i
                if (idx > all.length - 1) {
                  idx = idx - all.length - 1
                }
                if (!all[idx]().rejected) {
                  selectedAsset(all[idx])
                }
              }
            }
          }
        })
      )
    ), (data) => {
      if (!data || !data.parent_id) return null
      const parent = assets().find(a => a.id === data.parent_id)
      return parent ? `/api/image?file=${encodeURIComponent(parent.file_path)}` : null
    }),
    div({ class: 'flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6' },
      div({ class: 'flex items-center gap-4' },
        button({ class: 'btn btn-circle btn-ghost', onclick: () => goTo('/artwork-review') }, '←'),
        h1({ class: 'text-2xl md:text-3xl font-bold' }, 'Artwork Manager')
      ),
      div({ class: 'flex flex-wrap items-center gap-2 md:gap-4 w-full md:w-auto' },
        div({ class: 'form-control' },
          label({ class: 'label cursor-pointer gap-2' },
            span({ class: 'label-text' }, 'Show Rejected'),
            input({
              type: 'checkbox',
              class: 'toggle toggle-sm',
              checked: showRejected.bindAttr(),
              onchange: (e) => showRejected(e.target.checked)
            })
          )
        ),
        selectedPrompt.bindAs(p => button({
          class: `btn btn-sm ${p?.status === 'REJECTED' ? 'btn-success' : 'btn-error'} btn-outline`,
          onclick: handleRejectArtwork
        }, p?.status === 'REJECTED' ? 'Restore Artwork' : 'Reject Artwork')),
        button({ class: 'btn btn-outline btn-sm', onclick: fetchPromptAndAssets }, 'Refresh')
      )
    ),
    div({ class: 'flex-grow bg-base-100 rounded-xl border border-base-300 flex flex-col overflow-hidden shadow-sm' },
      selectedPrompt.bindAs(p => {
        if (!p) return div({ class: 'p-8 text-center opacity-50' }, 'Loading prompt details...')
        const promptsByModel = p.prompts_by_model || []
        const activeTab = fnstate(promptsByModel[0]?.model || null)
        return div({ class: 'p-4 border-b border-base-300 bg-base-200/50 flex flex-col md:flex-row justify-between items-start gap-4' },
          div({ class: 'space-y-2 flex-grow min-w-0' },
            div({ class: 'font-bold flex flex-col gap-1' },
              div({ class: 'flex items-center gap-2' },
                span({ class: 'badge badge-primary' }, p.display_title || p.product_name || 'Manual Prompt')
              ),
              p.display_title ? div({ class: 'text-xs opacity-50 font-normal italic' }, p.product_name) : ''
            ),
            p.user_input && p.user_input !== 'Manual Image Import'
              ? div({ class: 'text-sm' },
                span({ class: 'text-xs font-bold uppercase opacity-40 mr-1' }, 'Your input:'),
                span({ class: 'opacity-80' }, p.user_input)
              )
              : '',
            promptsByModel.length > 0
              ? div({ class: 'space-y-1' },
                promptsByModel.length > 1
                  ? div({ class: 'tabs tabs-sm tabs-box' },
                    ...promptsByModel.map(({ model }) =>
                      button({
                        class: activeTab.bindAttr(t => `tab${t === model ? ' tab-active' : ''}`),
                        onclick: () => activeTab(model)
                      }, model)
                    )
                  )
                  : div({ class: 'text-xs font-bold uppercase opacity-40' }, `Architect prompt (${promptsByModel[0].model}):`),
                activeTab.bindAs(t => {
                  const entry = promptsByModel.find(e => e.model === t)
                  return entry
                    ? div({ class: 'text-sm whitespace-pre-wrap opacity-70' }, entry.prompt)
                    : div()
                })
              )
              : div({ class: 'text-sm' },
                p.prompt_text
                  ? div({},
                    div({ class: 'text-xs font-bold uppercase opacity-40' }, 'Architect prompt:'),
                    div({ class: 'whitespace-pre-wrap opacity-70' }, p.prompt_text)
                  )
                  : p.user_input === 'Manual Image Import'
                    ? span({ class: 'opacity-40 italic' }, 'Manual Import')
                    : div({ class: 'flex items-center gap-2 opacity-40 italic' },
                      span({ class: 'loading loading-dots loading-xs' }),
                      'AI is crafting a descriptive prompt for your artwork...'
                    )
              )
          ),

          div({ class: 'flex flex-wrap items-center gap-4 w-full md:w-auto shrink-0' },
            ModelSelector({ model: genMoreModel, providers: ALL_IMAGE_PROVIDERS }),
            div({ class: 'join' },
              NumberSpinner({ state: batchSize, min: 1, max: 100, joinItem: true }),
              CreditButton({
                actionCode: 'IMAGE_GENERATION',
                class: 'btn btn-primary btn-sm join-item',
                label: 'Generate More',
                onclick: handleGenerateMore,
                multipliers: genMoreMultipliers
              })
            )
          )
        )
      }),
      AssetGrid({
        assets,
        selectedAsset,
        selectedAssets,
        showRejected,
        loading,
        toggleReject,
        handlePromote,
        handleEnhance,
        handleBulkPromote,
        handleBulkReject,
        productTypes
      })
    )
  )
  return rootEl
}
